import { BottomSheetFlatList, BottomSheetFlatListMethods } from '@gorhom/bottom-sheet'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef } from 'react'
import { LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent, Platform, View, ViewStyle } from 'react-native'

import { Chat as TChat } from '../../../../../models/communication/chat.store'
import { palette } from '../../../../../theme/palette'
import { ChatMessage } from './ChatMessage'

// cspell:ignore subviews
interface ChatMessagesListProps {
  messages: TChat[]
  footerClearance: number
  listHeight?: number
}

const AUTO_SCROLL_THRESHOLD_PX = 80

export const ChatMessagesList = observer(
  ({ messages, footerClearance, listHeight /* Don't default the height! */ }: ChatMessagesListProps) => {
    const listRef = useRef<BottomSheetFlatListMethods>(null)
    const isNearBottomRef = useRef(true)
    const didInitialScrollRef = useRef(false)

    const scrollToLast = useCallback((animated: boolean) => {
      listRef.current?.scrollToEnd({ animated })
    }, [])

    const renderItem = useCallback(
      ({ item, index }: { item: TChat; index: number }) => <ChatMessage firstItem={index === 0} chat={item} />,
      []
    )

    const keyExtractor = useCallback((item: TChat, index: number) => {
      if (item._id) return String(item._id)
      return `${item.timestamp ?? 0}-${item.userName ?? 'user'}-${index}`
    }, [])

    const onContentSizeChange = useCallback((/*contentWidth: number, contentHeight: number*/) => {
      if (isNearBottomRef.current || !didInitialScrollRef.current) {
        scrollToLast(false)
      }
    }, [scrollToLast])

    const onLayout = useCallback(
      (event: LayoutChangeEvent) => {
        // const { height } = event.nativeEvent.layout
        if (!didInitialScrollRef.current) {
          scrollToLast(false)
        }
      },
      [scrollToLast]
    )

    const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent
      const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height)
      isNearBottomRef.current = distanceFromBottom <= AUTO_SCROLL_THRESHOLD_PX
    }, [])

    useEffect(() => {
      if (messages.length === 0) return
      if (isNearBottomRef.current) {
        scrollToLast(false)
      }
    }, [messages, scrollToLast])

    useEffect(() => {
      if (messages.length === 0 || didInitialScrollRef.current) return

      scrollToLast(false)
      const frameId = requestAnimationFrame(() => {
        scrollToLast(false)
      })
      const timeoutId = setTimeout(() => {
        scrollToLast(false)
        didInitialScrollRef.current = true
      }, 180)

      return () => {
        cancelAnimationFrame(frameId)
        clearTimeout(timeoutId)
      }
    }, [messages.length, scrollToLast])

    return (
      <BottomSheetFlatList
        ref={listRef}
        style={{ flex: 1, height: listHeight }} // Don't default height!
        data={messages}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        onContentSizeChange={onContentSizeChange}
        onLayout={onLayout}
        onScroll={onScroll}
        scrollEventThrottle={16}
        keyboardShouldPersistTaps="handled"
        initialNumToRender={20}
        maxToRenderPerBatch={20}
        windowSize={9}
        removeClippedSubviews={Platform.OS === 'android'}
        contentContainerStyle={{
          backgroundColor: palette.cream,
          paddingHorizontal: 16,
          paddingBottom: footerClearance
        }}
        ListFooterComponent={<View style={{ height: 8 }} />}
      />
    )
  }
)
